import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from "react-router";

import { DesignSystemProvider } from "@icg360/design-system";

import { App, appLoader } from "./App";
import { Feature } from "./components/Feature";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import { LiftLock } from "./components/lift-lock/LiftLock";
import { LiftLockReturn } from "./components/lift-lock/LiftLockReturn";
import { LiftLockForm } from "./components/lift-lock/lift-lock-form/LiftLockForm";
import { Proposal } from "./components/proposal/Proposal";
import { GlobalProvider } from "./global-provider";
import "./main.module.css";
import { launchDarklyClient } from "./utils/feature-flags";
import { initTracking } from "./utils/tracking";

Sentry.init({
  dsn: `https://2dc85cd7915d2d623a13b14da037ed66@${String(import.meta.env.VITE_API_URL).replace("https://", "")}/telemetry/relay/4508961336918017`,
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  tracesSampleRate: 1.0
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

initTracking();

const router = sentryCreateBrowserRouter([
  {
    id: "root",
    path: "/:proposal",
    element: <App />,
    loader: appLoader,
    errorElement: <ErrorBoundary />,
    children: [
      { index: true, element: <Proposal /> },
      {
        path: "liftlock",
        element: (
          <Feature flag="liftLock" fallback={() => <Navigate to="/" />}>
            <LiftLock />
          </Feature>
        ),
        children: [
          { index: true, element: <LiftLockForm /> },
          { path: "return", element: <LiftLockReturn /> }
        ],
        errorElement: <ErrorBoundary />
      }
    ]
  },
  {
    path: "*",
    loader: ({ request }: { request: Request }) => {
      throw new Response(`Not found (${request.url})`, { status: 404 });
    },
    errorElement: <ErrorBoundary />
  }
]);

void (async () => {
  // Block render until feature flags are initialized or times out
  await launchDarklyClient;

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <GlobalProvider>
        <DesignSystemProvider>
          <RouterProvider router={router} />
        </DesignSystemProvider>
      </GlobalProvider>
    </React.StrictMode>
  );
})();
